// List view for /v1/discoveries with cursor "load more" + status filter.
// Filtering happens server-side via the `status` query param so the
// counts on the empty-state and the rendered list always match.

import { DiscoveryStatusBadge } from '@/components/discovery/DiscoveryStatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteDiscovery, useDiscoveriesList } from '@/hooks/useDiscoveries';
import type { Discovery, DiscoveryStatus, GraphConfidence } from '@/lib/discoveries';
import { Loader2, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

// One-line description from the result metrics, so a row reads as
// "7 relationships · 7 variables" instead of just a cryptic id. Null until
// the job has finished (no metrics yet).
function describe(d: Discovery): string | null {
  const m = d.job_metrics;
  if (!m || m.n_nodes == null) return null;
  const rels = (m.n_directed_edges ?? 0) + (m.n_undirected_edges ?? 0);
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  return `${plural(rels, 'relationship')} · ${plural(m.n_nodes, 'variable')}`;
}

// Compact colour band for the Graph confidence column — same thresholds as
// GraphConfidenceCard (emerald ≥75 / amber ≥50 / slate below). Kept in sync
// by hand; both read the server-computed `graph_confidence.score`.
function confidenceClass(score: number): string {
  if (score >= 75) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-slate-600 dark:text-slate-400';
}

// Inline confidence cell: the headline % + "n/m directed" sub-line. Renders a
// muted dash for runs with no confidence yet (queued/running/failed, or a
// legacy row whose result carried no numeric confidences).
function ConfidenceCell({ gc }: { gc?: GraphConfidence | null }) {
  if (!gc) return <span className="text-muted-foreground/60">—</span>;
  return (
    <div className="leading-tight">
      <span className={`text-sm font-semibold tabular-nums ${confidenceClass(gc.score)}`}>
        {gc.score}%
      </span>
      <div className="text-[11px] text-muted-foreground">
        {gc.directed}/{gc.total_edges} directed
      </div>
    </div>
  );
}

interface DiscoveryTableProps {
  status?: DiscoveryStatus | readonly DiscoveryStatus[];
  datasetId?: string;
  emptyState?: React.ReactNode;
  /** Refetch every 3s while any visible row is non-terminal. */
  enableActivePolling?: boolean;
}

// Stable identity for the effect dep that watches the status filter — the
// `status` prop may be a fresh array literal on every render, which would
// thrash the reset effect (clearing accumulated rows on every parent
// render). Joining to a string gives us value-equality semantics.
function statusKey(s: DiscoveryStatus | readonly DiscoveryStatus[] | undefined): string {
  if (s === undefined) return '';
  if (Array.isArray(s)) return s.join(',');
  return s as string;
}

export function DiscoveryTable({
  status,
  datasetId,
  emptyState,
  enableActivePolling,
}: DiscoveryTableProps) {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = React.useState<Discovery[]>([]);

  const statusFilterKey = statusKey(status);

  // Reset accumulator + cursor whenever the filter changes — otherwise
  // a tab switch keeps stale rows from the previous status query.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on filter change
  React.useEffect(() => {
    setAccumulated([]);
    setCursor(undefined);
  }, [statusFilterKey, datasetId]);

  const list = useDiscoveriesList({
    cursor,
    limit: 20,
    status,
    dataset_id: datasetId,
    enableActivePolling,
  });

  React.useEffect(() => {
    if (!list.data) return;
    if (cursor === undefined) {
      setAccumulated(list.data.items);
    } else {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((d) => d.id));
        return [...prev, ...list.data.items.filter((d) => !seen.has(d.id))];
      });
    }
  }, [list.data, cursor]);

  // Delete-flow hooks MUST be declared before any early returns — React's
  // rules-of-hooks enforces a stable call order between renders. Putting
  // them after `if (isLoading) return …` triggered React error #310 on
  // the second render when data finished loading. Fixed 2026-05-22.
  const deleteMut = useDeleteDiscovery();
  const [pendingDelete, setPendingDelete] = React.useState<Discovery | null>(null);

  if (list.isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (list.isError) {
    return (
      <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load discoveries — please refresh.
      </div>
    );
  }

  if (accumulated.length === 0) {
    return <>{emptyState}</>;
  }

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    const closeDialog = () => setPendingDelete(null);
    deleteMut.mutate(id, {
      onSuccess: () => {
        setAccumulated((prev) => prev.filter((d) => d.id !== id));
        toast.success('Discovery deleted');
        closeDialog();
      },
      onError: () => {
        toast.error('Could not delete discovery');
        closeDialog();
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Discovery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Dataset</TableHead>
              <TableHead>Knowledge</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {accumulated.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="max-w-md">
                  <Link to={`/discoveries/${d.id}`} className="block hover:underline">
                    {/* Agent-written title → dataset name → generic. */}
                    <span className="font-medium">
                      {d.title ?? d.dataset_name ?? 'Discovery'}
                    </span>
                  </Link>
                  {/* Agent's one-line summary if present, else the metric line. */}
                  <div className="line-clamp-2 text-xs text-muted-foreground">
                    {d.summary ?? describe(d) ?? ''}
                  </div>
                  <div className="text-[11px] text-muted-foreground/70">
                    {d.dataset_name && d.title ? <span>{d.dataset_name} · </span> : null}
                    <span className="font-mono">{d.id}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <DiscoveryStatusBadge status={d.status} />
                </TableCell>
                <TableCell>
                  <ConfidenceCell gc={d.graph_confidence} />
                </TableCell>
                <TableCell className="font-mono text-xs">
                  <Link to="/datasets" className="hover:underline">
                    {d.dataset_id}
                  </Link>
                </TableCell>
                <TableCell className="max-w-[16rem] text-sm">
                  {d.knowledge_id ? (
                    <Link
                      to={`/knowledge/${d.knowledge_id}/edit`}
                      className="block truncate hover:underline"
                      title={d.knowledge_name ?? d.knowledge_id}
                    >
                      {d.knowledge_name ?? d.knowledge_id}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {fmtDuration(d.duration_ms)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {fmtDate(d.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDelete(d)}
                    aria-label="Delete discovery"
                    title="Delete discovery"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(o: boolean) => !o && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this discovery?</DialogTitle>
            <DialogDescription>
              {pendingDelete ? (
                <>
                  Discovery <span className="font-mono text-xs">{pendingDelete.id}</span> will be
                  removed from your list. If it's still running we'll cancel it first. The result
                  data stays in cold storage (no refund of the quota credit).
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteMut.isPending}
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={handleConfirmDelete}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {list.data?.next_cursor ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={list.isFetching}
            onClick={() => setCursor(list.data?.next_cursor ?? undefined)}
          >
            {list.isFetching ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
